import clsx from "clsx";
import { useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronRight,
  faCircleCheck,
  faCircleQuestion,
  faCircleXmark,
} from "@awesome.me/kit-70c1f0055f/icons/duotone/solid";
import {
  type CaseReview as ApiCaseReview,
  type ProblemStatementRating,
} from "@/services/api/case-checking";
import { CC_ERROR_TEXT, CC_LABEL_FAILED, CC_PANEL, CC_SECTION_LABEL } from "@/components/case-checking/caseCheckingChrome";
import { groupByGateway } from "@/components/case-checking/problemStatementRuleDisplay";
import { CaseCheckingListRow } from "@/components/case-checking/CaseCheckingListRow";
import { SegmentedPillToggle } from "@/components/ui/segmented-tabs/SegmentedPillToggle";
import { ICON_CHECK_PASSED } from "@/components/case-checking/caseCheckingStatusBadges";
import {
  CaseReviewOverviewPanel,
  type CaseCheckingGrade,
} from "@/components/case-checking/CaseReviewOverviewPanel";
import {
  humanSignoffStatus,
  effectiveRowDotClass,
  effectiveRowRating,
  pillLabelForRating,
  problemStatementSkimText,
  RATING_DOT_CLASS,
} from "@/components/case-checking/problemStatementUi";
import {
  collectRuleIssues,
  countRuleIssues,
  filterRuleIssues,
  type RuleIssueFilter,
  type RuleIssueStatus,
} from "@/components/case-checking/problemStatementRuleIssues";
import { SummaryReviewBadge } from "@/components/case-checking/SummaryReviewBadge";
import {
  AgentHumanRuleCompare,
  HumanOverrideChip,
  PsStatementSummaryBody,
} from "@/components/case-checking/humanReviewCompare";
import { useHumanReviewOutcome } from "@/components/case-checking/useHumanReviewOutcome";
import { useProblemStatementRun } from "@/components/case-checking/useProblemStatementRun";
import { useHumanRuleReviews } from "@/components/case-checking/useHumanRuleReviews";
import {
  applyRuleOverridesToRollups,
  collectPsRuleChangeQuotes,
} from "@/components/case-checking/humanRuleReviewCascade";

type RatingFilter = ProblemStatementRating | "all";
type SummaryView = "statements" | "rules";

const FILTER_OPTIONS: {
  id: RatingFilter;
  label: string;
  dot?: string;
  rating?: ProblemStatementRating;
}[] = [
  { id: "all", label: "All" },
  { id: "pass", label: "Yes", dot: RATING_DOT_CLASS.pass, rating: "pass" },
  { id: "unclear", label: "Unclear", dot: RATING_DOT_CLASS.unclear, rating: "unclear" },
  { id: "fail", label: "No", dot: RATING_DOT_CLASS.fail, rating: "fail" },
  { id: "na", label: "N/A", dot: RATING_DOT_CLASS.na, rating: "na" },
];

const RULE_FILTER_OPTIONS: { id: RuleIssueFilter; label: string; dot?: string }[] = [
  { id: "all", label: "All issues" },
  { id: "failed", label: "Failed", dot: "bg-rose-500" },
  { id: "unclear", label: "For review", dot: "bg-amber-500" },
];

const VIEW_OPTIONS: { id: SummaryView; label: string }[] = [
  { id: "statements", label: "Outcomes" },
  { id: "rules", label: "Rule issues" },
];

function ruleIssueIcon(status: RuleIssueStatus) {
  if (status === "failed") return { icon: faCircleXmark, className: CC_LABEL_FAILED };
  return { icon: faCircleQuestion, className: "text-amber-400" };
}

export function ReviewSummaryTab({
  caseId,
  caseReview,
  grade,
  displayAdviser,
  fallbackAdviser,
  onSelectStatement,
  onSelectRuleIssue,
  companyId,
  isAgenticProcessing = false,
}: {
  caseId: string;
  caseReview?: ApiCaseReview | null;
  grade: CaseCheckingGrade;
  displayAdviser?: string;
  fallbackAdviser?: string;
  onSelectStatement: (statementId: string) => void;
  onSelectRuleIssue: (statementId: string, ruleId: string) => void;
  companyId?: string;
  isAgenticProcessing?: boolean;
}) {
  const { run, loading, error, scorable, humanReviewsById, narrativesById } = useProblemStatementRun(caseId);
  const { overridesByKey, reviewRun } = useHumanRuleReviews(caseId);
  const {
    outcomeGrade,
    reviewAttribution,
    humanReviewNotes,
    agentGrade,
    ruleRollupGrade,
    loading: outcomeLoading,
  } = useHumanReviewOutcome(caseId, caseReview, grade, displayAdviser, companyId);
  const effectiveScorable = useMemo(
    () => applyRuleOverridesToRollups(scorable, overridesByKey),
    [scorable, overridesByKey],
  );
  const effectiveScorableById = useMemo(
    () => new Map(effectiveScorable.map((r) => [r.id, r])),
    [effectiveScorable],
  );
  const [summaryView, setSummaryView] = useState<SummaryView>("statements");
  const [activeFilter, setActiveFilter] = useState<RatingFilter>("all");
  const [ruleFilter, setRuleFilter] = useState<RuleIssueFilter>("all");

  const counts = useMemo(() => {
    const c = { pass: 0, unclear: 0, fail: 0, na: 0 };
    for (const r of scorable) {
      const rating = effectiveRowRating(r, humanReviewsById.get(r.id), overridesByKey);
      if (rating in c) c[rating as keyof typeof c] += 1;
    }
    return c;
  }, [scorable, humanReviewsById, overridesByKey]);

  const signoffCounts = useMemo(() => {
    const counts = { confirmed: 0, overridden: 0, pending: 0 };
    for (const r of scorable) {
      const ruleChangeCount = (reviewRun?.reviews ?? []).filter(
        (review) => review.problem_statement_id === r.id && review.reviewed_at,
      ).length;
      const savedHuman = humanReviewsById.get(r.id);
      const signoff = humanSignoffStatus(r, savedHuman);
      const psOverridden =
        ruleChangeCount > 0 ||
        signoff === "overridden" ||
        effectiveRowRating(r, savedHuman, overridesByKey) !== r.rating;
      if (signoff === "pending" && !psOverridden) counts.pending += 1;
      else if (psOverridden) counts.overridden += 1;
      else counts.confirmed += 1;
    }
    return counts;
  }, [scorable, humanReviewsById, reviewRun?.reviews, overridesByKey]);

  const filtered = useMemo(() => {
    if (activeFilter === "all") return scorable;
    return scorable.filter(
      (r) => effectiveRowRating(r, humanReviewsById.get(r.id), overridesByKey) === activeFilter,
    );
  }, [activeFilter, scorable, humanReviewsById, overridesByKey]);

  const ruleIssues = useMemo(() => collectRuleIssues(scorable, overridesByKey), [scorable, overridesByKey]);
  const ruleIssueCounts = useMemo(() => countRuleIssues(ruleIssues), [ruleIssues]);
  const filteredRuleIssues = useMemo(
    () => filterRuleIssues(ruleIssues, ruleFilter),
    [ruleFilter, ruleIssues],
  );

  const viewToggleOptions = useMemo(
    () =>
      VIEW_OPTIONS.map((opt) => ({
        id: opt.id,
        label: `${opt.label} ${opt.id === "rules" ? ruleIssues.length : scorable.length}`,
      })),
    [ruleIssues.length, scorable.length],
  );

  const ratingFilterOptions = useMemo(
    () =>
      FILTER_OPTIONS.map((opt) => {
        const count =
          opt.id === "all"
            ? scorable.length
            : opt.rating
              ? counts[opt.rating as keyof typeof counts]
              : 0;
        return {
          id: opt.id,
          label: `${opt.label} ${count}`,
          disabled: opt.id !== "all" && count === 0,
        };
      }),
    [scorable.length, counts],
  );

  const ruleFilterOptions = useMemo(
    () =>
      RULE_FILTER_OPTIONS.map((opt) => {
        const count =
          opt.id === "all"
            ? ruleIssues.length
            : opt.id === "failed"
              ? ruleIssueCounts.failed
              : ruleIssueCounts.unclear;
        return {
          id: opt.id,
          label: `${opt.label} ${count}`,
          disabled: opt.id !== "all" && count === 0,
        };
      }),
    [ruleIssues.length, ruleIssueCounts],
  );

  if (loading || outcomeLoading) {
    return (
      <p className="text-sm text-slate-500">
        {isAgenticProcessing ? "Preparing review summary…" : "Loading review summary…"}
      </p>
    );
  }

  if (!run) {
    if (isAgenticProcessing) {
      return (
        <div className={CC_PANEL} data-testid="review-summary-empty">
          <p className="text-sm text-slate-600 dark:text-slate-300">
            Outcomes and rule results are not ready yet.
          </p>
          <p className="mt-1 text-sm text-slate-500">
            This tab will populate automatically once Marvin finishes the file check.
          </p>
        </div>
      );
    }

    return (
      <div className="space-y-3" data-testid="review-summary-tab">
        <CaseReviewOverviewPanel
          grade={outcomeGrade}
          caseReview={caseReview ?? null}
          displayAdviser={displayAdviser}
          fallbackAdviser={fallbackAdviser}
          showHumanReview={false}
          outcomeGrade={outcomeGrade}
          reviewAttribution={reviewAttribution}
          humanReviewNotes={humanReviewNotes}
          agentGrade={agentGrade}
          ruleRollupGrade={ruleRollupGrade}
        />
        <div className={CC_PANEL} data-testid="review-summary-empty">
          <p className="text-sm text-slate-600 dark:text-slate-300">No problem-statement run yet.</p>
          <p className="mt-1 text-sm text-slate-500">
            Outcomes appear here after the automated file check completes.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="review-summary-tab">
      <CaseReviewOverviewPanel
        grade={outcomeGrade}
        caseReview={caseReview ?? null}
        displayAdviser={displayAdviser}
        fallbackAdviser={fallbackAdviser}
        showHumanReview={false}
        outcomeGrade={outcomeGrade}
        reviewAttribution={reviewAttribution}
        humanReviewNotes={humanReviewNotes}
        agentGrade={agentGrade}
        ruleRollupGrade={ruleRollupGrade}
      />

      <div className="flex flex-wrap items-center gap-3" data-testid="review-summary-coverage">
        <SegmentedPillToggle
          options={viewToggleOptions}
          value={summaryView}
          onChange={setSummaryView}
          className="max-w-full flex-wrap"
          aria-label="Review summary view"
          data-testid="review-summary-view-toggle"
          optionTestIdPrefix="review-summary-view"
        />

        {summaryView === "statements" ? (
          <SegmentedPillToggle
            options={ratingFilterOptions}
            value={activeFilter}
            onChange={setActiveFilter}
            className="max-w-full flex-wrap"
            aria-label="Filter outcomes by rating"
            data-testid="review-summary-rating-filter"
            optionTestIdPrefix="review-summary-filter"
          />
        ) : (
          <SegmentedPillToggle
            options={ruleFilterOptions}
            value={ruleFilter}
            onChange={setRuleFilter}
            className="max-w-full flex-wrap"
            aria-label="Filter rule issues"
            data-testid="review-summary-rule-filter"
            optionTestIdPrefix="review-summary-rule-filter"
          />
        )}

        {summaryView === "statements" && scorable.length > 0 ? (
          <p className="text-xs text-slate-500" data-testid="review-summary-signoff-progress">
            {signoffCounts.confirmed} confirmed
            {signoffCounts.pending > 0 ? ` · ${signoffCounts.pending} pending` : ""}
            {signoffCounts.overridden > 0 ? ` · ${signoffCounts.overridden} overridden` : ""}
          </p>
        ) : null}
        {summaryView === "rules" && ruleIssues.length > 0 ? (
          <p className="text-xs text-slate-500" data-testid="review-summary-rule-progress">
            {ruleIssueCounts.failed} failed · {ruleIssueCounts.unclear} for review
          </p>
        ) : null}
      </div>

      {error ? <p className={clsx("text-sm", CC_ERROR_TEXT)}>{error}</p> : null}

      {summaryView === "statements" ? (
        <>
          <div className="space-y-4" data-testid="review-summary-statement-list">
          {groupByGateway(filtered).map((group) => (
          <div key={group.gateway ?? "all"} className="space-y-2">
          {group.label ? <p className={clsx(CC_SECTION_LABEL, "!mb-0")}>{group.label}</p> : null}
          <ul className="space-y-2">
            {group.items.map((rollup) => {
              const savedHuman = humanReviewsById.get(rollup.id);
              const signoff = humanSignoffStatus(rollup, savedHuman);
              const cascaded = effectiveScorableById.get(rollup.id);
              const effectiveRating = effectiveRowRating(rollup, savedHuman, overridesByKey);
              const ruleChangeCount = (reviewRun?.reviews ?? []).filter(
                (r) => r.problem_statement_id === rollup.id && r.reviewed_at,
              ).length;
              const psOverridden =
                ruleChangeCount > 0 ||
                signoff === "overridden" ||
                effectiveRating !== rollup.rating;
              const badgeStatus = psOverridden ? "overridden" : signoff;
              const ruleChanges = collectPsRuleChangeQuotes(rollup, overridesByKey);
              const narrative = narrativesById.get(rollup.id);
              const skimText = problemStatementSkimText(narrative);
              return (
                <CaseCheckingListRow
                  key={rollup.id}
                  as="li"
                  onClick={() => onSelectStatement(rollup.id)}
                  ariaLabel={`Open deep dive for ${rollup.title}`}
                  testId={`review-summary-row-${rollup.id}`}
                >
                  <span
                    aria-hidden
                    className={clsx(
                      "mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full transition-transform group-hover:scale-110",
                      effectiveRowDotClass(rollup, savedHuman, overridesByKey),
                    )}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-medium text-slate-900 dark:text-slate-100">{rollup.title}</span>
                      {psOverridden ? <HumanOverrideChip /> : null}
                    </span>
                    {rollup.assessment_question ? (
                      <span className="mt-1 block text-xs leading-relaxed text-slate-500">
                        {rollup.assessment_question}
                      </span>
                    ) : null}
                    <PsStatementSummaryBody
                      agentRating={rollup.rating}
                      effectiveRating={effectiveRating}
                      skimText={skimText}
                      ruleChanges={ruleChanges}
                      humanNotes={ruleChangeCount === 0 ? savedHuman?.notes : undefined}
                      isOverridden={psOverridden}
                      showStatementNow={
                        ruleChangeCount > 0 && Boolean(cascaded && cascaded.rating !== rollup.rating)
                      }
                      statementNowLabel={
                        cascaded ? `Statement now ${pillLabelForRating(cascaded.rating)}` : undefined
                      }
                    />
                  </span>
                  <span className="flex shrink-0 items-start gap-2 pt-0.5">
                    <SummaryReviewBadge status={badgeStatus} />
                    <FontAwesomeIcon
                      icon={faChevronRight}
                      className="hidden h-3 w-3 text-slate-600 transition-transform group-hover:translate-x-0.5 group-hover:text-slate-400 sm:block"
                    />
                  </span>
                </CaseCheckingListRow>
              );
            })}
          </ul>
          </div>
          ))}
          </div>

          {filtered.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-6">No statements match this filter.</p>
          ) : null}
        </>
      ) : (
        <>
          <ul className="space-y-2" data-testid="review-summary-rule-list">
            {filteredRuleIssues.map((issue) => {
              const { icon, className } = ruleIssueIcon(issue.status);
              const ruleOverride = overridesByKey.get(`${issue.statementId}::${issue.ruleId}`);
              const ruleNote = ruleOverride?.notes?.trim();
              return (
                <CaseCheckingListRow
                  key={`${issue.statementId}-${issue.ruleId}`}
                  as="li"
                  onClick={() => onSelectRuleIssue(issue.statementId, issue.ruleId)}
                  ariaLabel={`Open rule issue ${issue.label}`}
                  testId={`review-summary-rule-${issue.ruleId}`}
                >
                  <FontAwesomeIcon icon={icon} className={clsx("mt-0.5 h-4 w-4 shrink-0", className)} />
                  <span className="min-w-0 flex-1">
                    <span className="flex flex-wrap items-center gap-2">
                      <span className="block text-sm font-medium text-slate-900 dark:text-slate-100">{issue.label}</span>
                      {issue.humanChanged ? <HumanOverrideChip /> : null}
                    </span>
                    <span className="mt-1 block text-xs text-slate-500">
                      {issue.statementId} · {issue.statementTitle}
                    </span>
                    <span className="mt-1.5 block">
                      <AgentHumanRuleCompare
                        agentStatus={issue.agentStatus}
                        effectiveStatus={issue.status}
                      />
                    </span>
                    {issue.evidence ? (
                      <span className="mt-1.5 block text-xs leading-relaxed text-slate-400 line-clamp-2">
                        {issue.evidence}
                      </span>
                    ) : null}
                    {issue.humanChanged && ruleNote ? (
                      <span className="mt-1 block text-xs italic text-slate-400 line-clamp-2">
                        &ldquo;{ruleNote}&rdquo;
                      </span>
                    ) : null}
                  </span>
                  <span className="flex shrink-0 items-start gap-2 pt-0.5">
                    <span className="text-[11px] font-medium text-slate-500">{issue.ruleId}</span>
                    <FontAwesomeIcon
                      icon={faChevronRight}
                      className="hidden h-3 w-3 text-slate-600 transition-transform group-hover:translate-x-0.5 group-hover:text-slate-400 sm:block"
                    />
                  </span>
                </CaseCheckingListRow>
              );
            })}
          </ul>

          {filteredRuleIssues.length === 0 ? (
            <div className={clsx(CC_PANEL, "text-center py-6")} data-testid="review-summary-rule-empty">
              <FontAwesomeIcon icon={faCircleCheck} className={clsx("mx-auto h-5 w-5", ICON_CHECK_PASSED)} />
              <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">No failed or unclear rules.</p>
              <p className="mt-1 text-xs text-slate-500">Switch to Outcomes to sign off each outcome.</p>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}
